import React from "react";
import {
  View,
  StyleSheet,
  ScrollView,
  Text,
  useWindowDimensions,
  TouchableOpacity,
} from "react-native";
import Ionicons from "react-native-vector-icons/Ionicons";
import { TProcedure, TTabName } from "../../../../utils/types";
import { SessionContext } from "../../../../contexts";
import {
  getAfterImageUrls,
  getBeforeImageUrl,
  getClinicPlan,
  getPatientImages,
} from "../../../../services/api";
import { getClinicInfo } from "../../../../services/api-clinic";
import { logUIError } from "../../../../services/api-misc";
import { getJobMetadataPollingDiagnostics } from "../../../../services/jobMetadataPolling";
import {
  createSimulationDeadline,
  ImageUploadDiagnostics,
  getSimulationErrorMessage,
  getSimulationUserMessage,
  normalizeSimulationError,
  TSimulationDeadline,
  TSimulationStage,
} from "../../../../services/simulationPipeline";
import { mixpanel } from "../../../../utils/mixpanel";
import { APP_TOKENS as tokens } from "../../../../styles/tokens";
import {
  simulateTabBodyProcedure,
  simulateTabBreastProcedure,
  simulateTabFaceProcedure,
  simulateTabMommyMakeoverProcedure,
} from "./simulateTabConstants";

import Tab from "../../../common/native/Tab";
import {
  InputDropdown,
  ProcedureButton,
  TitleText,
  StickyOnboardingModal,
  simulateSteps,
  RainbowBorder,
  NormalText,
  PrimaryButton,
  SecondaryButton,
} from "../../../common/native";
import TryAI from "./TryAI";
import TryAILoading from "./TryAILoading";
import PastSimulationsSection, {
  TPastSimulationSelection,
} from "./PastSimulationsSection";
import { useOnboardingTips } from "../../../../contexts/OnboardingTipsContext";
import { useOnboardingSettings } from "../../../../contexts/OnboardingSettingsContext";
import { ModalOpener } from "../../../uncommon/native/modals";

type SimulateProps = {
  patientId?: string;
  suggestedImageUrls?: string[];
  showTitle?: boolean;
  showTipsToggle?: boolean;
  enableTips?: boolean;
};

type TSimulationErrorState = {
  message: string;
  isTimeout: boolean;
  isUploadFailure: boolean;
};

const trackSimulationEvent = (
  eventName: string,
  properties: Record<string, unknown>,
) => {
  try {
    mixpanel.track(eventName, properties);
  } catch (error) {
    console.warn(`Unable to track ${eventName}`, error);
  }
};

const Simulate: React.FC<SimulateProps> = ({
  patientId,
  suggestedImageUrls = [],
  showTitle = true,
  showTipsToggle = true,
  enableTips = true,
}) => {
  const userAppSession = React.useContext(SessionContext)?.session;
  const { width, height } = useWindowDimensions();
  const isMobile = Math.min(width, height) < 644;

  // Handle the state of showing tips or not
  const {
    tourActive,
    showSimulateTips,
    setShowSimulateTips,
    hasSeenWidgetDeployNudge,
    markWidgetDeployNudgeSeen,
  } = useOnboardingSettings();
  const [localTipsToggle, setLocalTipsToggle] = React.useState(false);
  const tipsEnabled = enableTips
    ? tourActive
      ? showSimulateTips
      : localTipsToggle
    : false;

  const scrollViewRef = React.useRef<ScrollView>(null);
  const [activeTab, setActiveTab] = React.useState<TTabName>("Breast");
  const [selectedProcedure, setSelectedProcedure] = React.useState<TProcedure>(
    "BREAST_AUGMENTATION",
  );
  const [currentImage, setCurrentImage] = React.useState<Blob | string>("");
  const [afterImageUrls, setAfterImageUrls] = React.useState<string[] | null>(
    null,
  );
  const [isShowSlider, setIsShowSlider] = React.useState<boolean>(false);
  const [afterImageMetaData, setAfterImageMetaData] = React.useState<any>(null);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [simulationError, setSimulationError] =
    React.useState<TSimulationErrorState | null>(null);
  const activeDeadlineRef = React.useRef<TSimulationDeadline | null>(null);
  const isMountedRef = React.useRef(true);

  const [isAllowScrolling, setIsAllowScrolling] = React.useState<boolean>(true);
  const [patientSuggestedImageUrls, setPatientSuggestedImageUrls] =
    React.useState<string[]>([]);
  const [showUpgradePrompt, setShowUpgradePrompt] = React.useState(false);
  const [showDeployWidgetNudge, setShowDeployWidgetNudge] =
    React.useState(false);
  const [isKnownOutOfFreeSimulations, setIsKnownOutOfFreeSimulations] =
    React.useState(false);
  const [freeSimulationsUsedCount, setFreeSimulationsUsedCount] =
    React.useState(0);
  const [isWidgetDeployed, setIsWidgetDeployed] = React.useState<
    boolean | null
  >(userAppSession?.deployStatus ? true : null);
  const { tipsState, markStepComplete } = useOnboardingTips();

  const tabNames: TTabName[] = ["Breast", "Body", "Head", "Mommy Makeover"];

  React.useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      activeDeadlineRef.current?.cancel();
      activeDeadlineRef.current = null;
    };
  }, []);

  const shouldHighlightDropdown =
    isMobile &&
    tipsState.selectSecondProcedure &&
    !tipsState.simulationTipsCompleted;

  React.useEffect(() => {
    if (suggestedImageUrls.length > 0) {
      setPatientSuggestedImageUrls(suggestedImageUrls);
      return;
    }

    const fetchPatientSuggestedImages = async () => {
      if (!userAppSession || !patientId) {
        setPatientSuggestedImageUrls([]);
        return;
      }

      try {
        const patientImages = await getPatientImages(
          userAppSession,
          patientId,
          {
            uploadTypes: [
              "PatientImageForForm",
              "PatientImageForUser",
              "BeforeAfterImageMetadata",
              "SideBySideImage",
            ],
          },
        );

        const dedupedUrls = Array.from(
          new Set(
            patientImages
              .map((item) => item.presigned_urls?.[0])
              .filter((url): url is string => Boolean(url)),
          ),
        );

        setPatientSuggestedImageUrls(dedupedUrls);
      } catch (error) {
        console.error("Error fetching patient suggested images:", error);
        logUIError({
          message: "Failed to fetch patient suggested images",
          page_url:
            typeof window !== "undefined" ? window.location.href : undefined,
          user_email: userAppSession?.userEmail || undefined,
          clinic_name: userAppSession?.clinicName || undefined,
          clinic_id: userAppSession?.clinicId || undefined,
          context: {
            component: "HomeSimulate",
            patientId,
            selectedProcedure,
            error: error instanceof Error ? error.message : String(error),
          },
        }).catch((logErr) =>
          console.warn("logUIError failed in HomeSimulate", logErr),
        );
        setPatientSuggestedImageUrls([]);
      }
    };

    fetchPatientSuggestedImages();
  }, [patientId, suggestedImageUrls.length]);

  React.useEffect(() => {
    let isMounted = true;
    const accessToken = userAppSession?.accessToken;
    const clinicId = userAppSession?.clinicId;

    if (!accessToken || !clinicId) {
      setIsWidgetDeployed(null);
      return;
    }

    const fetchWidgetDeploymentStatus = async () => {
      try {
        const clinicInfo = await getClinicInfo(accessToken, clinicId);
        if (!isMounted) return;
        setIsWidgetDeployed(Boolean(clinicInfo.widget_deployed));
      } catch (error) {
        console.warn(
          "Unable to fetch widget deployment status. Falling back to session status.",
          error,
        );
        if (!isMounted) return;
        setIsWidgetDeployed(userAppSession.deployStatus ? true : null);
      }
    };

    void fetchWidgetDeploymentStatus();

    return () => {
      isMounted = false;
    };
  }, [
    userAppSession?.accessToken,
    userAppSession?.clinicId,
    userAppSession?.deployStatus,
  ]);

  const maybeShowWidgetDeployNudge = React.useCallback(async () => {
    if (!userAppSession?.clinicId) return;
    if (!userAppSession?.accessToken) return;
    if (userAppSession.planType !== "FREE") return;
    if (isWidgetDeployed === true) return;
    if (hasSeenWidgetDeployNudge(userAppSession.clinicId)) return;

    let usedCount = freeSimulationsUsedCount;
    try {
      const billingSummary = await getClinicPlan({
        accessToken: userAppSession.accessToken,
        clinicId: userAppSession.clinicId,
      });
      usedCount = Math.max(0, billingSummary.free_simulations_used_count ?? 0);
      setFreeSimulationsUsedCount(usedCount);
    } catch (error) {
      console.warn(
        "Unable to refresh free simulation usage count for deploy nudge.",
        error,
      );
      // Fallback assumes current successful simulation increments count by 1.
      usedCount = freeSimulationsUsedCount + 1;
    }

    if (usedCount < 3) return;

    let widgetDeployed: boolean | null = isWidgetDeployed;
    if (widgetDeployed === null) {
      try {
        const clinicInfo = await getClinicInfo(
          userAppSession.accessToken,
          userAppSession.clinicId,
        );
        widgetDeployed = Boolean(clinicInfo.widget_deployed);
        setIsWidgetDeployed(widgetDeployed);
      } catch (error) {
        console.warn(
          "Unable to confirm widget deployment status at nudge threshold.",
          error,
        );
        widgetDeployed = userAppSession.deployStatus ? true : false;
      }
    }

    if (widgetDeployed) return;

    markWidgetDeployNudgeSeen(userAppSession.clinicId);
    setShowDeployWidgetNudge(true);
    mixpanel.track("Deploy Widget Nudge Shown", {
      "User Email": userAppSession.userEmail,
      "Clinic Name": userAppSession.clinicName,
      "Simulation Count Before Nudge": usedCount,
    });
  }, [
    freeSimulationsUsedCount,
    hasSeenWidgetDeployNudge,
    isWidgetDeployed,
    markWidgetDeployNudgeSeen,
    userAppSession,
  ]);

  const shouldBlockSimulationForFreeLimit = React.useCallback(
    async (signal: AbortSignal) => {
      if (isKnownOutOfFreeSimulations) {
        return true;
      }

      if (!userAppSession?.accessToken || !userAppSession?.clinicId) {
        return false;
      }

      const getBillingSummary = async () => {
        const billingSummary = await getClinicPlan(
          {
            accessToken: userAppSession.accessToken,
            clinicId: userAppSession.clinicId,
          },
          { signal },
        );
        return billingSummary;
      };

      try {
        const billingSummary = await getBillingSummary();
        setFreeSimulationsUsedCount(
          Math.max(0, billingSummary.free_simulations_used_count ?? 0),
        );
        const blocked =
          billingSummary.plan_type === "FREE" &&
          (billingSummary.remaining_free_simulation_count ?? 0) <= 0;
        if (blocked) {
          setIsKnownOutOfFreeSimulations(true);
        }
        return blocked;
      } catch (error) {
        if (
          error &&
          typeof error === "object" &&
          "name" in error &&
          error.name === "AbortError"
        ) {
          throw error;
        }
        console.warn("Unable to confirm free simulation balance.", error);
        return false;
      }
    },
    [isKnownOutOfFreeSimulations, userAppSession],
  );

  const runSimulation = React.useCallback(
    async (image: Blob | string) => {
      if (isLoading) return;

      activeDeadlineRef.current?.cancel();
      const deadline = createSimulationDeadline();
      activeDeadlineRef.current = deadline;

      let uploadDiagnostics: ImageUploadDiagnostics | undefined;
      let simulationSucceeded = false;
      let stage: TSimulationStage = "validation";
      const simulationType =
        typeof image === "string" ? "ClinicOS Model" : "Photo Upload";
      setSimulationError(null);
      setIsLoading(true);

      try {
        if (!userAppSession) {
          throw new Error("Missing user session");
        }

        stage = "billing_check";
        const shouldBlockSimulation = await shouldBlockSimulationForFreeLimit(
          deadline.signal,
        );
        if (shouldBlockSimulation) {
          setIsKnownOutOfFreeSimulations(true);
          setShowUpgradePrompt(true);
          return;
        }

        setIsShowSlider(false);
        trackSimulationEvent("Simulation Started", {
          "Simulation Attempt ID": deadline.attemptId,
          "User Email": userAppSession.userEmail,
          "Clinic Name": userAppSession.clinicName,
          "Patient ID": patientId,
          "Simulation Type": simulationType,
          Procedure: selectedProcedure,
        });

        let beforeImageUrl;
        if (typeof image === "string") {
          beforeImageUrl = image;
        } else {
          beforeImageUrl = await getBeforeImageUrl(
            userAppSession,
            image,
            selectedProcedure,
            deadline.controller,
            (nextStage) => {
              stage = nextStage;
            },
            (diagnostics) => {
              uploadDiagnostics = diagnostics;
            },
          );
        }

        const afterImageUrlsAndMetaData = await getAfterImageUrls(
          userAppSession,
          beforeImageUrl,
          selectedProcedure,
          deadline.controller,
          patientId,
          (nextStage) => {
            stage = nextStage;
          },
        );

        setAfterImageMetaData({
          ...afterImageUrlsAndMetaData.metaData,
          imageData: afterImageUrlsAndMetaData.imageData,
        });
        setAfterImageUrls(afterImageUrlsAndMetaData.imageUrls);
        simulationSucceeded = true;
        stage = "completed";

        const successProperties = {
          "Simulation Attempt ID": deadline.attemptId,
          "User Email": userAppSession.userEmail,
          "Clinic Name": userAppSession.clinicName,
          "Patient ID": patientId,
          "Simulation Type": simulationType,
          Procedure: selectedProcedure,
          "Elapsed Milliseconds": deadline.elapsedMs(),
          "Image Upload": uploadDiagnostics,
        };
        trackSimulationEvent("Simulation Succeeded", successProperties);
        trackSimulationEvent("SimulationComplete", successProperties);

        if (tipsState.selectModel && !tipsState.selectSecondProcedure) {
          markStepComplete("selectModel");
        }
        if (tipsState.selectSecondProcedure) {
          setTimeout(() => {
            markStepComplete("selectSecondProcedure");
          }, 5000); // delay by 5 seconds
        }
      } catch (error: unknown) {
        const normalizedError = normalizeSimulationError({
          error,
          stage,
          timedOut: deadline.isTimedOut(),
        });
        const pollingDiagnostics = getJobMetadataPollingDiagnostics(error);

        if (normalizedError.code === "SIMULATION_CANCELLED") return;

        const rawErrorMessage = getSimulationErrorMessage(error);
        const userMessage = getSimulationUserMessage(normalizedError);

        if (isMountedRef.current) {
          setSimulationError({
            message:
              normalizedError.stage === "image_upload"
                ? "Your photo could not be uploaded. It is still selected. Try again, or switch between Wi-Fi and mobile data."
                : userMessage,
            isUploadFailure: normalizedError.stage === "image_upload",
            isTimeout: normalizedError.isTimeout,
          });
        }
        console.error("Error fetching data:", error);
        const logMessage = normalizedError.isTimeout
          ? "Simulation timed out"
          : `Simulation generation failed${
              normalizedError.s3Key ? ` (S3 key: ${normalizedError.s3Key})` : ""
            }${
              normalizedError.uploadAttempts
                ? ` after ${normalizedError.uploadAttempts} upload attempts`
                : ""
            }`;
        const browserOnline =
          typeof navigator !== "undefined" ? navigator.onLine : undefined;
        const userAgent =
          typeof navigator !== "undefined" ? navigator.userAgent : undefined;
        void logUIError({
          message: logMessage,
          page_url:
            typeof window !== "undefined" ? window.location.href : undefined,
          user_email: userAppSession?.userEmail || undefined,
          clinic_name: userAppSession?.clinicName || undefined,
          clinic_id: userAppSession?.clinicId || undefined,
          stack_trace:
            error instanceof Error ? error.stack : normalizedError.stack,
          context: {
            component: "HomeSimulate",
            simulation_attempt_id: deadline.attemptId,
            patientId,
            selectedProcedure,
            currentImageType: typeof image === "string" ? "url" : "blob",
            stage: normalizedError.stage,
            error_code: normalizedError.code,
            elapsed_ms: deadline.elapsedMs(),
            timeout_ms: deadline.timeoutMs,
            is_timeout: normalizedError.isTimeout,
            http_status: normalizedError.httpStatus,
            s3_key: normalizedError.s3Key,
            s3_host: normalizedError.s3Host,
            upload_attempts: normalizedError.uploadAttempts,
            upload_failure_history: normalizedError.uploadFailureHistory,
            upload_diagnostics: uploadDiagnostics,
            backend_error_code: normalizedError.backendCode,
            backend_failure_type: normalizedError.backendFailureType,
            backend_retryable: normalizedError.backendRetryable,
            image_size_bytes:
              typeof image === "string" ? undefined : image.size,
            image_mime_type: typeof image === "string" ? undefined : image.type,
            browser_online: browserOnline,
            user_agent: userAgent,
            error: rawErrorMessage,
            ...(pollingDiagnostics ?? {}),
          },
        }).catch((logErr) =>
          console.warn("logUIError failed in HomeSimulate", logErr),
        );

        trackSimulationEvent("Simulation Error", {
          "Simulation Attempt ID": deadline.attemptId,
          "User Email": userAppSession?.userEmail,
          "Clinic Name": userAppSession?.clinicName,
          "Patient ID": patientId,
          "Simulation Type": simulationType,
          Procedure: selectedProcedure,
          Stage: normalizedError.stage,
          "Error Code": normalizedError.code,
          "Error Message": rawErrorMessage,
          "Elapsed Milliseconds": deadline.elapsedMs(),
          "Timeout Milliseconds": deadline.timeoutMs,
          "Is Timeout": normalizedError.isTimeout,
          "HTTP Status": normalizedError.httpStatus,
          "S3 Key": normalizedError.s3Key,
          "Upload Attempts": normalizedError.uploadAttempts,
          "Backend Error Code": normalizedError.backendCode,
          "Backend Failure Type": normalizedError.backendFailureType,
          "Backend Retryable": normalizedError.backendRetryable,
        });
      } finally {
        deadline.finish();
        if (activeDeadlineRef.current === deadline) {
          activeDeadlineRef.current = null;
        }
        if (isMountedRef.current) setIsLoading(false);
        if (simulationSucceeded) {
          if (isMountedRef.current) setIsShowSlider(true);
          void maybeShowWidgetDeployNudge();
        }
      }
    },
    [
      isLoading,
      markStepComplete,
      patientId,
      selectedProcedure,
      shouldBlockSimulationForFreeLimit,
      maybeShowWidgetDeployNudge,
      tipsState.selectModel,
      tipsState.selectSecondProcedure,
      userAppSession,
    ],
  );

  const handleProcedurePress = (procedure: TProcedure) => {
    setSelectedProcedure(procedure);
    setIsShowSlider(false);

    // Update onboarding tips state
    if (tipsState.selectProcedure && !tipsState.selectModel) {
      markStepComplete("selectProcedure");
    }
  };

  const saveCurrentImage = React.useCallback(
    (image: Blob | string) => {
      setCurrentImage(image);
      runSimulation(image);
    },
    [runSimulation],
  );

  const handleChooseAnotherImage = React.useCallback(() => {
    activeDeadlineRef.current?.cancel();
    activeDeadlineRef.current = null;
    setSimulationError(null);
    setCurrentImage("");
    setAfterImageUrls(null);
    setAfterImageMetaData(null);
    setIsShowSlider(false);
  }, []);

  const handleSetIsAllowScrolling = (bool: boolean) => {
    setIsAllowScrolling(bool);
  };

  const handleOnTabPress = (tab: TTabName) => {
    // Update onboarding tips state for second procedure
    // if (tipsState.selectSecondProcedure) {
    //   markStepComplete("selectSecondProcedure");
    // }

    setActiveTab(tab);

    // Delay setting procedure to ensure correct ref assignment
    setTimeout(() => {
      if (tab === "Breast") {
        setSelectedProcedure("BREAST_AUGMENTATION");
      } else if (tab === "Body") {
        setSelectedProcedure("BBL");
      } else if (tab === "Head") {
        setSelectedProcedure("LIP_FILLER");
      } else if (tab === "Mommy Makeover") {
        setSelectedProcedure("TUMMY_TUCK_AND_BREAST_LIFT");
      }
    }, 0);
  };

  const getTabForProcedure = (procedure: TProcedure): TTabName => {
    if (simulateTabBodyProcedure.includes(procedure)) return "Body";
    if (simulateTabFaceProcedure.includes(procedure)) return "Head";
    if (simulateTabMommyMakeoverProcedure.includes(procedure))
      return "Mommy Makeover";
    return "Breast";
  };

  const handleSelectPastSimulation = React.useCallback(
    (selection: TPastSimulationSelection) => {
      if (selection.frameUrls.length === 0) return;

      const procedure = selection.procedureTypes[0] as TProcedure | undefined;
      if (procedure) {
        setSelectedProcedure(procedure);
        setActiveTab(getTabForProcedure(procedure));
      }

      setAfterImageUrls(selection.frameUrls);
      setAfterImageMetaData({
        success: true,
        reason: undefined,
        implant_size_in_cc: null,
        imageData: { id: Number(selection.imageId) },
      });
      setIsShowSlider(true);
      scrollViewRef.current?.scrollTo({ y: 0, animated: true });

      mixpanel.track("Past Simulation Opened In Viewer", {
        "User Email": userAppSession?.userEmail,
        "Clinic Name": userAppSession?.clinicName,
        Procedure: procedure ?? "unknown",
        "Frame Count": selection.frameUrls.length,
      });
    },
    [userAppSession],
  );

  // Turn off tips if the user has completed the simulation tips
  React.useEffect(() => {
    if (enableTips && tipsState.simulationTipsCompleted) {
      setShowSimulateTips(false);
    }
  }, [enableTips, tipsState.simulationTipsCompleted, setShowSimulateTips]);

  if (!userAppSession) return null;

  if (simulationError) {
    return (
      <View
        style={styles.simulationErrorContainer}
        accessibilityRole="alert"
        accessibilityLiveRegion="assertive"
      >
        <Text style={styles.simulationErrorTitle}>
          {simulationError.isTimeout
            ? "Simulation timed out"
            : "Simulation unsuccessful"}
        </Text>
        <Text style={styles.simulationErrorMessage}>
          {simulationError.message}
        </Text>
        <View style={styles.simulationErrorActions}>
          <PrimaryButton
            text={
              simulationError.isUploadFailure ? "Retry Upload" : "Try Again"
            }
            onPress={() => {
              if (currentImage) void runSimulation(currentImage);
            }}
            disabled={!currentImage || isLoading}
            loading={isLoading}
            accessibilityLabel={
              simulationError.isUploadFailure
                ? "Retry upload"
                : "Try the simulation again"
            }
          />
          <SecondaryButton
            text="Choose Another Image"
            onPress={handleChooseAnotherImage}
            disabled={isLoading}
            accessibilityLabel="Choose another image for the simulation"
          />
        </View>
      </View>
    );
  }

  if (isMobile) {
    return (
      <View style={{ flex: 1 }}>
        <ModalOpener
          visible={showUpgradePrompt}
          modalType="UpgradePrompt"
          onClose={() => setShowUpgradePrompt(false)}
          upgradePromptVariant="freeSimulationLimit"
        />
        <ModalOpener
          visible={showDeployWidgetNudge}
          modalType="DeployWidgetNudge"
          onClose={() => setShowDeployWidgetNudge(false)}
        />
        <ScrollView
          ref={scrollViewRef}
          showsHorizontalScrollIndicator={false}
          showsVerticalScrollIndicator={false}
          scrollEnabled={isAllowScrolling}
        >
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            {showTitle ? (
              <TitleText
                text="Simulate"
                style={{ marginTop: 5, marginBottom: 5 }}
              />
            ) : (
              <View />
            )}
            {showTipsToggle ? (
              <TouchableOpacity
                onPress={() => setLocalTipsToggle((v) => !v)}
                style={{ padding: 6 }}
              >
                <Ionicons
                  name="information-circle-outline"
                  size={24}
                  color="#00B7F4"
                />
                <NormalText
                  text={localTipsToggle ? "Hide tips" : "Show tips"}
                  fontWeight="500"
                />
              </TouchableOpacity>
            ) : null}
          </View>

          <View style={{ paddingHorizontal: 20 }}>
            <View style={{ zIndex: 1, marginBottom: 15, width: "100%" }}>
              <RainbowBorder
                isActive={tipsEnabled && shouldHighlightDropdown}
                borderRadius={12}
              >
                <InputDropdown
                  name={""}
                  selections={[
                    ...simulateTabBreastProcedure,
                    ...simulateTabBodyProcedure,
                    ...simulateTabFaceProcedure,
                    ...simulateTabMommyMakeoverProcedure,
                  ]}
                  value={selectedProcedure}
                  onSelect={(value) => {
                    setIsShowSlider(false);
                    setSelectedProcedure(value as TProcedure);

                    if (tipsState.selectSecondProcedure) {
                      markStepComplete("selectSecondProcedure");
                      markStepComplete("simulationTipsCompleted");
                    }
                  }}
                />
              </RainbowBorder>
            </View>
            {isLoading ? (
              <TryAILoading currentImage={currentImage} isMobile={isMobile} />
            ) : (
              <TryAI
                isShowSlider={isShowSlider}
                selectedProcedure={selectedProcedure}
                afterImageUrls={afterImageUrls}
                afterImageMetaData={afterImageMetaData}
                saveCurrentImage={saveCurrentImage}
                isMobile={isMobile}
                suggestedImageUrls={patientSuggestedImageUrls}
                handleSetIsAllowScrolling={handleSetIsAllowScrolling}
              />
            )}
            <PastSimulationsSection
              session={userAppSession}
              patientId={patientId}
              refreshTrigger={afterImageUrls}
              onSelectPastSimulation={handleSelectPastSimulation}
            />
          </View>
        </ScrollView>
        {tipsEnabled && showTipsToggle && (
          <StickyOnboardingModal
            steps={simulateSteps}
            autoAdvanceOnMobileStep="selectProcedure"
          />
        )}
      </View>
    );
  }

  return (
    <View style={{ flex: 1 }}>
      <ModalOpener
        visible={showUpgradePrompt}
        modalType="UpgradePrompt"
        onClose={() => setShowUpgradePrompt(false)}
        upgradePromptVariant="freeSimulationLimit"
      />
      <ModalOpener
        visible={showDeployWidgetNudge}
        modalType="DeployWidgetNudge"
        onClose={() => setShowDeployWidgetNudge(false)}
      />
      <ScrollView
        ref={scrollViewRef}
        showsHorizontalScrollIndicator={false}
        showsVerticalScrollIndicator={false}
      >
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            paddingHorizontal: 20,
          }}
        >
          {showTitle ? (
            <TitleText
              text="Simulate"
              style={{ marginTop: 5, marginBottom: 5 }}
            />
          ) : (
            <View />
          )}
          {showTipsToggle ? (
            <TouchableOpacity
              onPress={() => setLocalTipsToggle((v) => !v)}
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 6,
                padding: 6,
              }}
            >
              <Ionicons
                name="information-circle-outline"
                size={18}
                color="#00B7F4"
              />
              <NormalText
                text={localTipsToggle ? "Hide tips" : "Show tips"}
                fontWeight="500"
              />
            </TouchableOpacity>
          ) : null}
        </View>

        <Tab
          currentTab={activeTab}
          disabledTabIndexes={
            isLoading ? tabNames.map((_, index) => index) : []
          }
          tabNames={tabNames}
          highlightedTabIndex={
            tipsEnabled && tipsState.selectSecondProcedure ? 1 : undefined
          } // 👈 highlight "Body"
          onTabPress={(tab) => handleOnTabPress(tab)}
          tabContent={[
            <View key={1} style={styles.tabContainer}>
              <View style={{ flexDirection: "row", gap: 20 }}>
                <View style={{ gap: 20, marginLeft: 4 }}>
                  {simulateTabBreastProcedure.map((procedure, index) => (
                    <ProcedureButton
                      key={procedure}
                      procedure={procedure}
                      selected={procedure === selectedProcedure}
                      onPress={handleProcedurePress}
                      disabled={isLoading}
                      highlight={
                        tipsEnabled &&
                        tipsState.selectProcedure &&
                        !tipsState.selectModel &&
                        index === 1
                      }
                    />
                  ))}
                </View>
                {isLoading ? (
                  <TryAILoading currentImage={currentImage} isMobile={false} />
                ) : (
                  <TryAI
                    isShowSlider={isShowSlider}
                    selectedProcedure={selectedProcedure}
                    afterImageUrls={afterImageUrls}
                    afterImageMetaData={afterImageMetaData}
                    saveCurrentImage={saveCurrentImage}
                    isMobile={isMobile}
                    suggestedImageUrls={patientSuggestedImageUrls}
                  />
                )}
              </View>
            </View>,
            <View key={2} style={styles.tabContainer}>
              <View style={{ flexDirection: "row", gap: 20 }}>
                <View style={{ gap: 20, marginLeft: 4 }}>
                  {simulateTabBodyProcedure.map((procedure) => (
                    <ProcedureButton
                      key={procedure}
                      procedure={procedure}
                      selected={procedure === selectedProcedure}
                      onPress={handleProcedurePress}
                      disabled={isLoading}
                    />
                  ))}
                </View>
                {isLoading ? (
                  <TryAILoading currentImage={currentImage} isMobile={false} />
                ) : (
                  <TryAI
                    isShowSlider={isShowSlider}
                    selectedProcedure={selectedProcedure}
                    afterImageUrls={afterImageUrls}
                    afterImageMetaData={afterImageMetaData}
                    saveCurrentImage={saveCurrentImage}
                    isMobile={isMobile}
                    suggestedImageUrls={patientSuggestedImageUrls}
                  />
                )}
              </View>
            </View>,
            <View key={3} style={styles.tabContainer}>
              <View style={{ flexDirection: "row", gap: 20 }}>
                <View style={{ gap: 20, marginLeft: 4 }}>
                  {simulateTabFaceProcedure.map((procedure) => (
                    <ProcedureButton
                      key={procedure}
                      procedure={procedure}
                      selected={procedure === selectedProcedure}
                      onPress={handleProcedurePress}
                      disabled={isLoading}
                    />
                  ))}
                </View>
                {isLoading ? (
                  <TryAILoading currentImage={currentImage} isMobile={false} />
                ) : (
                  <TryAI
                    isShowSlider={isShowSlider}
                    selectedProcedure={selectedProcedure}
                    afterImageUrls={afterImageUrls}
                    afterImageMetaData={afterImageMetaData}
                    saveCurrentImage={saveCurrentImage}
                    isMobile={isMobile}
                    suggestedImageUrls={patientSuggestedImageUrls}
                  />
                )}
              </View>
            </View>,
            <View key={4} style={styles.tabContainer}>
              <View style={{ flexDirection: "row", gap: 20 }}>
                <View style={{ gap: 20, marginLeft: 4 }}>
                  {simulateTabMommyMakeoverProcedure.map((procedure) => (
                    <ProcedureButton
                      key={procedure}
                      procedure={procedure}
                      selected={procedure === selectedProcedure}
                      onPress={handleProcedurePress}
                      disabled={isLoading}
                    />
                  ))}
                </View>
                {isLoading ? (
                  <TryAILoading currentImage={currentImage} isMobile={false} />
                ) : (
                  <TryAI
                    isShowSlider={isShowSlider}
                    selectedProcedure={selectedProcedure}
                    afterImageUrls={afterImageUrls}
                    afterImageMetaData={afterImageMetaData}
                    saveCurrentImage={saveCurrentImage}
                    isMobile={isMobile}
                    suggestedImageUrls={patientSuggestedImageUrls}
                  />
                )}
              </View>
            </View>,
          ]}
        />
        <View style={styles.desktopPastSimulationsWrap}>
          <PastSimulationsSection
            session={userAppSession}
            patientId={patientId}
            refreshTrigger={afterImageUrls}
            onSelectPastSimulation={handleSelectPastSimulation}
          />
        </View>
      </ScrollView>
      {tipsEnabled && showTipsToggle && (
        <StickyOnboardingModal steps={simulateSteps} />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  tabContainer: {
    flexDirection: "column",
    alignItems: "stretch",
    paddingTop: 15,
    height: "auto",
  },
  desktopPastSimulationsWrap: {
    paddingHorizontal: 20,
  },
  simulationErrorContainer: {
    alignSelf: "center",
    alignItems: "center",
    width: "100%",
    maxWidth: 480,
    margin: "auto",
    padding: tokens.spacing.xxl,
    gap: tokens.spacing.lg,
    borderWidth: tokens.borderWidth.thin,
    borderColor: tokens.color.errorBorder,
    borderRadius: tokens.radius.lg,
    backgroundColor: tokens.color.errorSurface,
  },
  simulationErrorTitle: {
    color: tokens.color.errorText,
    fontFamily: tokens.font.semibold,
    fontSize: tokens.fontSize.lg,
    lineHeight: tokens.lineHeight.lg,
    textAlign: "center",
  },
  simulationErrorMessage: {
    color: tokens.color.textPrimary,
    fontFamily: tokens.font.regular,
    fontSize: tokens.fontSize.md,
    lineHeight: tokens.lineHeight.md,
    textAlign: "center",
  },
  simulationErrorActions: {
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "row",
    flexWrap: "wrap",
    gap: tokens.spacing.md,
  },
});

export default Simulate;
